import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';

const DeviceCard = ({ device, syncStatus, onConfigure, onTest, onDelete }) => {
  const deviceIcons = {
    garmin: 'Watch',
    apple_watch: 'Watch',
    whoop: 'Activity',
    hume: 'Watch',
    polar: 'Heart'
  };

  const deviceColors = {
    garmin: { text: 'text-blue-500', bg: 'bg-blue-500/10' },
    apple_watch: { text: 'text-gray-700', bg: 'bg-gray-700/10' },
    whoop: { text: 'text-red-500', bg: 'bg-red-500/10' },
    hume: { text: 'text-purple-500', bg: 'bg-purple-500/10' },
    polar: { text: 'text-cyan-500', bg: 'bg-cyan-500/10' }
  };

  const getStatusColor = () => {
    if (syncStatus === 'testing') return 'text-warning';
    if (syncStatus === 'success') return 'text-success';
    if (syncStatus === 'error') return 'text-error';
    return device?.isActive ? 'text-success' : 'text-muted-foreground';
  };

  const getStatusText = () => {
    if (syncStatus === 'testing') return 'Testing...';
    if (syncStatus === 'success') return 'Connected';
    if (syncStatus === 'error') return 'Connection Failed';
    return device?.isActive ? 'Active' : 'Inactive';
  };

  const color = deviceColors?.[device?.deviceType] || { text: 'text-primary', bg: 'bg-primary/10' };

  return (
    <motion.div
      className="bg-card rounded-xl p-6 elevation-2 border border-wellness-light hover:elevation-3 transition-all duration-200"
      whileHover={{ y: -4 }}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      {/* Device Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-3 rounded-xl ${color?.bg}`}>
            <Icon name={deviceIcons?.[device?.deviceType] || 'Watch'} size={24} className={color?.text} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{device?.deviceName}</h3>
            <p className="text-sm text-muted-foreground capitalize">{device?.deviceType?.replace('_', ' ')}</p>
          </div>
        </div>
        <div className={`flex items-center space-x-1 ${getStatusColor()}`}>
          <div className="w-2 h-2 rounded-full bg-current animate-pulse"></div>
          <span className="text-xs font-medium">{getStatusText()}</span>
        </div>
      </div>
      {/* Device Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <Icon name="Battery" size={16} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Battery</span>
          </div>
          <p className="text-lg font-semibold text-foreground">{device?.batteryLevel || 85}%</p>
        </div>
        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Last Sync</span>
          </div>
          <p className="text-sm font-semibold text-foreground">
            {device?.lastSyncAt ? format(new Date(device?.lastSyncAt), 'MMM d, HH:mm') : 'Never'}
          </p>
        </div>
      </div>
      {/* Data Metrics */}
      <div className="mb-4">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Activity" size={16} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Synced Metrics</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {device?.syncedMetrics?.map((metric) => (
            <span
              key={metric}
              className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium"
            >
              {metric}
            </span>
          )) || (
            <>
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">Heart Rate</span>
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">Steps</span>
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">Calories</span>
            </>
          )}
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onConfigure}
          className="flex-1"
        >
          <Icon name="Settings" size={16} className="mr-1" />
          Configure
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onTest}
          disabled={syncStatus === 'testing'}
        >
          <Icon name="Zap" size={16} />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onDelete}
          className="text-error hover:bg-error/10"
        >
          <Icon name="Trash2" size={16} />
        </Button>
      </div>
    </motion.div>
  );
};

export default DeviceCard;
